import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertBanner,
    Button,
    DropdownDescriptiveMenu,
    DropdownMenu,
    Label,
    SegmentedControl,
    SparkIcon,
    Spinner,
    TooltipTrigger,
} from '@spark';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { previewTruncationMessage } from '@/features/datasets/utils/datasetUploadFlow';
import {
    fieldValuesFromUploadEntry,
    sampleRecordsFromUploadEntry,
} from '@/features/datasets/utils/filePreviewUtils';
import TrajectoryFieldsMapper from './trajectory/TrajectoryFieldsMapper';
import {
    TRAJECTORY_SAMPLE_ROWS,
    emptyTrajectoryDetails,
    trajectoryYesDetails,
} from './trajectory/trajectoryMapping';
import FieldPreviewCard from './FieldPreviewCard';
import MapPromptTemplateModal from './promptMapper/MapPromptTemplateModal';
import { mappedFieldsCount } from './promptMapper/promptMappingUtils';
import {
    LABELS_DELIMITER_OPTIONS,
    labelsDelimiterOptionLabel,
} from '@/features/datasets/utils/labelsDelimiter';
import { formatOriginalColumnPreview } from '@/features/datasets/utils/nestedFieldValue';
import LabelMappingRows from './LabelMappingRows';
import EmptyResponsesSection from './EmptyResponsesSection';
import EmptyTrajectoriesSection from './EmptyTrajectoriesSection';
import {
    EMPTY_TRAJECTORIES_DEFAULT,
    isEmptyTrajectoryGenerateWithAi,
} from '@/features/datasets/utils/emptyTrajectoriesHandling';
import MissingLabelsFixMenu from './MissingLabelsFixMenu';
import NoLabelsModeSection from './NoLabelsModeSection';
import { emptyResponseDefault } from '@/features/datasets/utils/emptyResponsesHandling';
import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_UNLABELED,
    hasLabelsSwitchLabel,
} from '@/features/datasets/utils/hasLabelsHandling';
import { defaultLabelColumn } from './columnDefaults';

/** Figma Prompt-Mapper 616:80705 / 617:81077 / 618:81798 / 600:77020 */
const PROMPT_INSTRUCTIONS_QUESTION = 'Does this field contain prompt instructions?';
const PROMPT_INSTRUCTIONS_SUBTITLE =
    'Tell us which part of the prompt contains the input for the model';
const PROMPT_INSTRUCTIONS_QUESTION_TOOLTIP =
    "If the field includes prompt instructions, we'll separate them from the input that changes between examples";
const PROMPT_INSTRUCTIONS_NO_TOOLTIP = 'No, it contains only the input';
const PROMPT_INSTRUCTIONS_YES_TOOLTIP = 'Yes, it includes instructions';
const PROMPT_INSTRUCTIONS_EDIT_LABEL = 'Edit Template';
const PROMPT_INSTRUCTIONS_EDIT_TOOLTIP =
    'The Template defines how to separate the prompt instructions from the input that changes between examples.';
const PROMPT_INSTRUCTIONS_DELETE_LABEL = 'Delete template';
const SELECT_INPUT_FIELD_FIRST = 'Select an input field first';

/** Saved mapping summary under the question (Figma 600:77020). */
function promptMappingSummary(primary, template) {
    const count = mappedFieldsCount(template);
    return `Primary: ${primary} · ${count} fields mapped`;
}

/** Agentic tasks: Figma 4012:19158 (sic — copy matches the design). */
export const HAS_TRAJECTORIES_QUESTION = 'Does your data contains trajectories?';
/**
 * Under the question when answered No and no response / output column is
 * mapped: every row gets a reasoning path and answer from the teacher model.
 */
export const GENERATE_TRAJECTORIES_HINT =
    "We'll generate the reasoning paths and answers for every row with your teacher model.";
/**
 * Under the question when answered No while a response / output column is
 * mapped: the original response is kept only as a reference.
 */
export const GENERATE_TRAJECTORIES_WITH_REFERENCE_HINT =
    "We'll generate the reasoning paths and answers together. Your original response is kept as a reference but won't be used for training.";
/** Under the Map Trajectory Fields block when empty trajectories are filled with AI. */
export const EMPTY_TRAJECTORIES_DEFERRED_NOTE =
    "Trajectory generation starts after you finish the 'Model configuration' step.";
/** Same slot when no model provider is configured (Figma 4024:24838). */
export const NO_PROVIDER_TRAJECTORIES_HINT =
    'Connect at least one model provider to use this agentic task in the ';
export const NO_PROVIDER_SETTINGS_LINK_LABEL = 'Settings Page';

/**
 * "Does your data contain …?" question + Yes/No segmented control on one row
 * (Figma 4012:19134 / 4012:19156): 398px question, 28px control, Yes first.
 * `hint` renders 8px under the row, aligned with the control.
 */
const YesNoQuestionRow = ({ question, value, onChange, testId, hint, children }) => (
    <div
        className={['cw-dc-yes-no', hint ? 'cw-dc-yes-no--with-hint' : ''].filter(Boolean).join(' ')}
        data-testid={testId}
    >
        <div className="cw-dc-yes-no__row">
            <p className="cw-dc-yes-no__question">{question}</p>
            <SegmentedControl
                size="small"
                className="cw-dc-yes-no__control"
                items={[
                    { id: 'yes', label: 'Yes' },
                    { id: 'no', label: 'No' },
                ]}
                activeId={value ? 'yes' : 'no'}
                onChange={(id) => {
                    const next = id === 'yes';
                    // Re-selecting the active answer must not reset the block.
                    if (next !== Boolean(value)) onChange(next);
                }}
            />
        </div>
        {hint}
        {children}
    </div>
);

/**
 * Hint under the trajectories question once answered No: trajectories will
 * be generated with AI, which needs a configured model provider — without
 * one the hint becomes the Settings deep-link message (Figma 4024:24154 /
 * 4024:24838). The caller keeps it unmounted while the provider catalog is
 * still loading so the message never flashes on first paint.
 */
const NoTrajectoriesHint = ({ hasNoProviders, hasResponseColumn, onOpenProviderSettings }) => {
    if (!hasNoProviders) {
        return (
            <p className="cw-dc-yes-no__hint" data-testid="generate-trajectories-hint">
                {hasResponseColumn ? GENERATE_TRAJECTORIES_WITH_REFERENCE_HINT : GENERATE_TRAJECTORIES_HINT}
            </p>
        );
    }
    return (
        <p className="cw-dc-yes-no__hint" data-testid="no-provider-trajectories-hint">
            {NO_PROVIDER_TRAJECTORIES_HINT}
            <Button
                variant="link"
                size="tiny"
                className="cw-dc-yes-no__hint-link"
                onClick={onOpenProviderSettings}
            >
                {NO_PROVIDER_SETTINGS_LINK_LABEL}
            </Button>
        </p>
    );
};

const DATA_CREATION_OPTIONS = [
    {
        value: 'human_created',
        label: 'Human created',
        description: 'A human reviewed the results and confirmed they’re accurate.',
    },
    {
        value: 'commercial_model',
        label: 'LLM created',
        description: 'Created by an LLM and not reviewed or approved by a human.',
    },
    {
        value: 'other',
        label: 'Other',
        description: 'Created using another method or source.',
    },
];

/** How many records to sample for the Review Examples / Summaries shuffle preview. */
const FIELD_PREVIEW_SAMPLE_SIZE = 20;

/** Extract `{{field_name}}` dynamic fields from a prompt template. */
export function dynamicFieldsFromTemplate(template) {
    return [...new Set([...(template || '').matchAll(/\{\{\s*([\w.-]+)\s*\}\}/g)].map((m) => m[1]))];
}

const FieldRow = ({ title, titleTooltip, description, children }) => (
    <div className="cw-dc-field-row">
        <div className="cw-dc-field-row__label">
            {titleTooltip ? (
                <h3 className="cw-dc-section-title cw-dc-section-title--with-help">
                    <span>{title}</span>
                    <TooltipTrigger text={titleTooltip}>
                        <span className="cw-help-icon">
                            <SparkIcon slug="circle-help" size="small" />
                        </span>
                    </TooltipTrigger>
                </h3>
            ) : (
                <h3 className="cw-dc-section-title">{title}</h3>
            )}
            {description && <p className="cw-dc-field-row__desc">{description}</p>}
        </div>
        <div className="cw-dc-field-row__controls">{children}</div>
    </div>
);

/**
 * Single-select DropdownMenu in trigger-search (combobox) mode: when open,
 * typing in the trigger input filters the options (the current selection
 * stays visible as the placeholder), ↑/↓ move the highlight, and Enter or
 * click selects and closes the panel.
 */
const SearchableSelect = ({ searchEmptyText = 'No matches', ...props }) => (
    <DropdownMenu
        selectType="single-select"
        size="medium"
        searchable
        searchMode="trigger"
        searchEmptyText={searchEmptyText}
        {...props}
    />
);

const ColumnFieldSelect = ({ columnsLoading = false, error, ...props }) => (
    <div
        className={`cw-dc-select-360${columnsLoading ? ' cw-dc-select-360--loading' : ''}`}
        aria-busy={columnsLoading || undefined}
    >
        <SearchableSelect
            {...props}
            disabled={columnsLoading || props.disabled}
            prefixIcon={columnsLoading ? <Spinner size="small" /> : props.prefixIcon}
            placeholder={columnsLoading ? 'Loading columns...' : props.placeholder}
            text={columnsLoading ? undefined : props.text}
            items={columnsLoading ? [] : props.items}
            searchEmptyText={columnsLoading ? 'Loading columns...' : props.searchEmptyText}
        />
        {error ? (
            <div className="cw-field-error" role="alert">
                {error}
            </div>
        ) : null}
    </div>
);

/**
 * Per-target dataset details group of the wizard Data Collection step
 * (Figma 3080-5248 / Prompt-Mapper 616-80705): Data Creation, Review model
 * input (input field select + prompt-instructions Yes/No + preview), Review
 * Extracted Labels / Entities (classification & NER), and Review Summaries /
 * Outputs (generative). Rendered once when "share the same details" is
 * checked, twice (with a title suffix) otherwise.
 */
const DatasetDetailsForm = ({
    titleSuffix = '',
    taskType,
    isMultilabel,
    slot,
    details,
    onPatch,
    availableLabels,
    replaceLabelOptions,
    onLabelColumnChange,
    onToggleTaskLabel,
    errors = {},
    /**
     * False until empty response values are confirmed (or a completed scan is
     * unverifiable). Hidden while columns or a remote Hugging Face / library
     * empty-scan is still pending so the radios do not flash then disappear.
     */
    showEmptyResponsesSection = true,
    /** When set (multi-file overlap), dropdowns use this list instead of the primary file. */
    availableColumns,
    /** Hugging Face / file column discovery is in flight — column selects show a loading outline. */
    columnsLoading = false,
    /** After ingest, opting out of labels should return to unlabeled import. */
    preferUnlabeledOnOptOut = false,
    /** A file exceeds the 20MB preview limit — extracted labels/entities come from the first 20MB only. */
    previewTruncated = false,
    /**
     * Rendered from the Add Data modal on an existing task (vs the create-task
     * wizard) — switches the Fix-menu update-catalog copy to "in task".
     */
    isExistingTask = false,
    /**
     * Sync the task label catalog: add these unknown extracted
     * labels/entities and, in the create-task wizard only, drop catalog
     * entries extracted in no uploaded file.
     */
    onAddTaskLabels,
    /** Open the Generate-with-AI flow from the missing-labels Fix menu. */
    onGenerateNewData,
    /** Open the OS file picker for this form's upload target. */
    onAddMoreFiles,
    /**
     * Agentic task (autonomy level ≠ 1): shows the trajectories question and
     * the Map Trajectory Fields block after Review Outputs.
     */
    isAgentic = false,
    /** Provider catalog loaded with no configured model provider (agentic No branch). */
    hasNoProviders = false,
    /** Provider catalog still loading — the No-branch hint stays hidden meanwhile. */
    providersLoading = false,
    /** Open Settings > Model Providers from the no-provider hint link. */
    onOpenProviderSettings,
    /**
     * Agentic Yes branch: false until empty trajectory values are confirmed
     * (or a completed scan is unverifiable), same gating as
     * `showEmptyResponsesSection` but for the mapped trajectory column.
     */
    showEmptyTrajectoriesSection = false,
}) => {
    const isClassification = taskType === TaskType.classification;
    const isNer = taskType === TaskType.NER;
    const isGenerative = isGenerativeTask(taskType);
    const isContentGeneration = taskType === TaskType.contentGeneration;
    const showLabelsSection = isClassification || isNer;
    const showResponseSection = showLabelsSection || isGenerative;
    const columns = availableColumns ?? slot?.columns ?? [];
    const suffix = titleSuffix ? ` ${titleSuffix}` : '';
    const hasLabels = details.hasLabels !== false;
    const noLabelsMode = details.noLabelsMode || NO_LABELS_MODE_DEFAULT;

    const labelsSectionTitle = isNer
        ? `Review Extracted Entities${suffix}`
        : `Review Extracted Labels${suffix}`;

    // Multilabel classification: labels-delimiter select (auto-detected from
    // the first records, user-editable) + raw label value preview card.
    const isMultilabelClassification = isClassification && Boolean(isMultilabel);
    const showDelimiterSelect =
        isMultilabelClassification && Boolean(details.labelColumn) && !columnsLoading;

    const outputSectionTitle = isContentGeneration
        ? `Review Outputs${suffix}`
        : `Review Summaries${suffix}`;
    const outputFieldLabel = isContentGeneration ? 'Select output field' : 'Select Summary field';
    const outputPreviewLabel = isContentGeneration ? 'Output' : 'Summary';
    const outputDescription = isContentGeneration ? 'Select output field' : 'Select summary field';

    const [previewText, setPreviewText] = useState(null);
    const [primaryPreviewText, setPrimaryPreviewText] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [responsePreviewIndex, setResponsePreviewIndex] = useState(0);
    const [mapperOpen, setMapperOpen] = useState(false);

    const exampleColumn = details.exampleColumn;
    const exampleValues = useMemo(
        () => fieldValuesFromUploadEntry(slot, exampleColumn, FIELD_PREVIEW_SAMPLE_SIZE),
        [slot, exampleColumn]
    );

    // Raw values of the response column (labels for classification/NER,
    // summary/output for generative) — drives both the generative Output
    // preview card and the multilabel "Raw Value" card.
    const responseColumn = details.labelColumn;
    const responseValues = useMemo(() => {
        const formatted = fieldValuesFromUploadEntry(
            slot,
            responseColumn,
            FIELD_PREVIEW_SAMPLE_SIZE
        );
        if (formatted.length) return formatted;
        const fromRaw = fieldValuesFromUploadEntry(
            slot,
            responseColumn,
            FIELD_PREVIEW_SAMPLE_SIZE,
            { raw: true }
        )
            .map((value) => formatOriginalColumnPreview(value))
            .filter((text) => text && String(text).trim());
        if (fromRaw.length) return fromRaw;
        // Library samples sometimes omit the labels column until mappings
        // exist; the extracted list is still a valid cell value to preview.
        return (details.extractedLabels || [])
            .map((label) => formatOriginalColumnPreview(label))
            .filter((text) => text && String(text).trim());
    }, [slot, responseColumn, details.extractedLabels]);

    // Reset to the first sample whenever the column or file content changes.
    useEffect(() => {
        setPreviewIndex(0);
    }, [slot?.content, slot?.format, slot?.sampleRecord, slot?.sampleRecords, exampleColumn]);

    useEffect(() => {
        setResponsePreviewIndex(0);
    }, [slot?.content, slot?.format, slot?.sampleRecord, slot?.sampleRecords, responseColumn]);

    const selectedRawValue =
        exampleValues.length > 0
            ? exampleValues[Math.min(previewIndex, exampleValues.length - 1)]
            : null;

    const responsePreviewText =
        responseValues.length > 0
            ? responseValues[Math.min(responsePreviewIndex, responseValues.length - 1)]
            : null;

    const handleShuffleExample = () => {
        if (exampleValues.length < 2) return;
        setPreviewIndex((current) => {
            let next = Math.floor(Math.random() * exampleValues.length);
            if (next === current) {
                next = (current + 1) % exampleValues.length;
            }
            return next;
        });
    };

    const handleShuffleResponse = () => {
        if (responseValues.length < 2) return;
        setResponsePreviewIndex((current) => {
            let next = Math.floor(Math.random() * responseValues.length);
            if (next === current) {
                next = (current + 1) % responseValues.length;
            }
            return next;
        });
    };

    // The Example card always shows the selected raw record value. When a
    // prompt template is saved, additionally ask the backend to extract the
    // primary-input value with the template (same endpoint and semantics as
    // the upload: `extracted_example` is the example field's own value) for
    // the Primary-input card.
    const primaryModelInput = details.primaryModelInput;
    useEffect(() => {
        let cancelled = false;
        setPreviewText(selectedRawValue);
        const template = details.promptTemplate || '';
        const fields = dynamicFieldsFromTemplate(template);
        if (!selectedRawValue || !template || fields.length === 0) {
            setPrimaryPreviewText(null);
            return undefined;
        }
        // Same field the upload submit sends as example_dynamic_field_name.
        const exampleField =
            primaryModelInput && fields.includes(primaryModelInput)
                ? primaryModelInput
                : fields.includes('example')
                  ? 'example'
                  : fields[0];
        fetch('/api/preview-field-extraction', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                content: selectedRawValue,
                prompt_template: template,
                dynamic_fields: fields,
                example_field_name: exampleField,
            }),
        })
            .then((res) => res.json())
            .then((result) => {
                if (cancelled) return;
                setPrimaryPreviewText((result?.success && result.extracted_example) || null);
            })
            .catch(() => {
                if (!cancelled) setPrimaryPreviewText(null);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedRawValue, details.promptTemplate, primaryModelInput]);

    // Labels configured in task creation (Business Goals step) that no
    // Replace-or-create value covers — surfaced as a non-blocking warning
    // banner (Figma 3088-7897). Dropped rows (mapping = null) don't count.
    // The chosen fallback label is excluded: it's expected not to appear
    // among the labels extracted from the file.
    const mappedValues = new Set(
        (details.extractedLabels || [])
            .filter((label) => details.labelMappings?.[label] !== null)
            .map((label) => details.labelMappings?.[label] ?? label)
    );
    const missingLabels =
        details.labelsLoading ||
        columnsLoading ||
        !(details.extractedLabels || []).length
            ? []
            : (availableLabels || []).filter(
                  (label) => !mappedValues.has(label) && label !== details.rejectionLabel
              );

    const catalogSet = new Set(availableLabels || []);
    const unknownExtractedLabels = (details.extractedLabels || []).filter((label) => {
        if (details.labelMappings?.[label] === null) return false;
        const mapped = details.labelMappings?.[label] ?? label;
        return !catalogSet.has(label) && !catalogSet.has(mapped);
    });

    // Dismissal is keyed by the missing set, so the banner comes back when
    // the set of missing labels changes after being dismissed.
    const missingSignature = missingLabels.join('\u0000');
    const [dismissedSignature, setDismissedSignature] = useState(null);
    const showMissingBanner = missingLabels.length > 0 && dismissedSignature !== missingSignature;

    const handleUpdateTaskLabels = () => {
        onAddTaskLabels?.(unknownExtractedLabels);
        // On an existing task the catalog is add-only, so labels covered by
        // the task's other files stay "missing" from this file forever —
        // clicking the fix acknowledges them. The wizard prunes them from the
        // catalog instead, which empties the missing set on its own.
        if (isExistingTask) setDismissedSignature(missingSignature);
        if (!unknownExtractedLabels.length) return;
        onPatch({
            labelMappings: {
                ...details.labelMappings,
                ...Object.fromEntries(unknownExtractedLabels.map((label) => [label, label])),
            },
        });
    };

    const columnItems = (selected, onSelect) => [
        ...columns.map((column) => ({
            label: column,
            state: column === selected ? 'selected' : 'default',
            onClick: () => onSelect(column),
        })),
    ];

    // Saved prompt mapping (Map prompt template modal). Yes/No is derived from
    // a saved template (or an open mapper) so Cancel without Save returns to No.
    const hasSavedPrompt = Boolean(details.promptTemplate);
    const promptInstructionsYes = hasSavedPrompt || mapperOpen;

    const handlePromptSave = ({ template, customFields, primaryField }) => {
        onPatch({
            customInstructions: true,
            promptTemplate: template,
            promptCustomFields: customFields,
            primaryModelInput: primaryField,
        });
        setMapperOpen(false);
    };

    const handlePromptDelete = () => {
        setMapperOpen(false);
        onPatch({
            customInstructions: false,
            promptTemplate: '',
            promptCustomFields: {},
            primaryModelInput: null,
        });
    };

    const handlePromptInstructionsChange = (id) => {
        if (id === 'yes') {
            setMapperOpen(true);
            return;
        }
        setMapperOpen(false);
        if (hasSavedPrompt) {
            handlePromptDelete();
        }
    };

    const handleHasLabelsChange = (checked) => {
        if (checked) {
            const nextLabel = defaultLabelColumn(taskType, columns);
            onPatch({
                hasLabels: true,
                noLabelsMode: NO_LABELS_MODE_DEFAULT,
                aiLabels: [],
            });
            // Opt-out stored labelColumn as '' and may have a stale
            // extractedFor mark — re-default through the column handler so
            // extraction runs again.
            if (nextLabel) {
                onLabelColumnChange(nextLabel);
            }
        } else {
            const nextMode = preferUnlabeledOnOptOut
                ? NO_LABELS_MODE_UNLABELED
                : NO_LABELS_MODE_DEFAULT;
            onPatch({
                hasLabels: false,
                noLabelsMode: nextMode,
                labelColumn: '',
                extractedLabels: [],
                labelMappings: {},
                aiLabels: [],
            });
        }
    };

    const handleHasTrajectoriesChange = (yes) => {
        if (!yes) {
            onPatch(emptyTrajectoryDetails());
            return;
        }
        // Same defaults the panel seeds for agentic uploads: a `messages`
        // column when the file has one, keys pre-picked from its sample rows.
        onPatch(
            trajectoryYesDetails(sampleRecordsFromUploadEntry(slot, TRAJECTORY_SAMPLE_ROWS), columns)
        );
    };

    return (
        <div className="cw-dc-details-group">
            <FieldRow
                title={`Data Creation${suffix}`}
                titleTooltip="We prioritize human authenticity. Behind the scenes, our system assigns a higher reliability score to human-generated data compared to LLM-generated inputs."
                description="Who created the dataset & benchmark?"
            >
                <div className="cw-dc-select-360 cw-dc-data-creation">
                    <Label label="Data was created by" showAsterisk={false} showIcon={false} />
                    <DropdownDescriptiveMenu
                        panelLayout="overlay"
                        placeholder="Select"
                        value={
                            DATA_CREATION_OPTIONS.find((o) => o.value === details.dataCreation)
                                ?.label
                        }
                        items={DATA_CREATION_OPTIONS.map((option) => ({
                            title: option.label,
                            description: option.description,
                        }))}
                        onSelect={(item, index) =>
                            onPatch({ dataCreation: DATA_CREATION_OPTIONS[index].value })
                        }
                        size="medium"
                        panelClassName="cw-dc-data-creation-dropdown-panel"
                    />
                </div>
            </FieldRow>

            <FieldRow
                title={`Review model input${suffix}`}
                description="Select input field"
            >
                <div className="cw-dc-review-examples">
                    <div className="cw-dc-review-examples__inputs">
                        <ColumnFieldSelect
                            columnsLoading={columnsLoading}
                            error={errors.exampleColumn}
                            label="Select input field"
                            showAsterisk
                            placeholder="Select"
                            searchEmptyText="No matching columns"
                            text={exampleColumn || undefined}
                            items={columnItems(exampleColumn, (column) =>
                                onPatch({ exampleColumn: column })
                            )}
                        />
                    </div>
                    <div className="cw-dc-preview-stack">
                        {hasSavedPrompt && primaryPreviewText && (
                            <FieldPreviewCard
                                label="Primary input (Example)"
                                text={primaryPreviewText}
                                columnName={primaryModelInput}
                                showShuffle={false}
                            />
                        )}
                        <FieldPreviewCard
                            label={hasSavedPrompt ? 'Raw input field content' : 'Example'}
                            text={previewText}
                            values={exampleValues}
                            rowIndex={previewIndex}
                            rowTotal={exampleValues.length}
                            columnName={exampleColumn}
                            onShuffle={handleShuffleExample}
                            shuffleAriaLabel="Shuffle example"
                        />
                    </div>
                </div>
            </FieldRow>
            <div className="cw-dc-prompt-instructions">
                {/* Same row as the other Yes/No questions (Figma 4012:19124):
                    398px question, 24px gap, 104px Small outlined control. */}
                <div className="cw-dc-yes-no__row">
                    <p className="cw-dc-yes-no__question">
                        <TooltipTrigger text={PROMPT_INSTRUCTIONS_QUESTION_TOOLTIP}>
                            <span>{PROMPT_INSTRUCTIONS_QUESTION}</span>
                        </TooltipTrigger>
                    </p>
                    {hasSavedPrompt ? (
                        <div className="cw-dc-prompt-instructions__actions">
                            <TooltipTrigger text={PROMPT_INSTRUCTIONS_EDIT_TOOLTIP}>
                                <Button
                                    size="small"
                                    variant="secondary"
                                    startIcon={<SparkIcon slug="edit" size="small" />}
                                    onClick={() => setMapperOpen(true)}
                                >
                                    {PROMPT_INSTRUCTIONS_EDIT_LABEL}
                                </Button>
                            </TooltipTrigger>
                            <Button
                                size="tiny"
                                variant="secondary"
                                icon={<SparkIcon slug="trash" size="small" />}
                                aria-label={PROMPT_INSTRUCTIONS_DELETE_LABEL}
                                onClick={handlePromptDelete}
                            />
                        </div>
                    ) : (
                        <SegmentedControl
                            size="small"
                            className="cw-dc-yes-no__control"
                            items={[
                                {
                                    id: 'yes',
                                    label: 'Yes',
                                    disabled: !exampleColumn,
                                    tooltip: exampleColumn
                                        ? PROMPT_INSTRUCTIONS_YES_TOOLTIP
                                        : SELECT_INPUT_FIELD_FIRST,
                                },
                                {
                                    id: 'no',
                                    label: 'No',
                                    tooltip: PROMPT_INSTRUCTIONS_NO_TOOLTIP,
                                },
                            ]}
                            activeId={promptInstructionsYes ? 'yes' : 'no'}
                            onChange={handlePromptInstructionsChange}
                        />
                    )}
                </div>
                <p className="cw-dc-prompt-instructions__subtitle">
                    {hasSavedPrompt
                        ? promptMappingSummary(
                              details.primaryModelInput,
                              details.promptTemplate
                          )
                        : PROMPT_INSTRUCTIONS_SUBTITLE}
                </p>
            </div>
            {mapperOpen && (
                <MapPromptTemplateModal
                    slot={slot}
                    exampleColumn={exampleColumn}
                    columns={columns}
                    initial={
                        details.promptTemplate
                            ? {
                                  template: details.promptTemplate,
                                  customFields: details.promptCustomFields || {},
                                  primaryField: details.primaryModelInput || null,
                              }
                            : null
                    }
                    onSave={handlePromptSave}
                    onCancel={() => setMapperOpen(false)}
                />
            )}

            {showResponseSection && (
                <YesNoQuestionRow
                    testId="has-labels-question"
                    question={
                        <>
                            {hasLabelsSwitchLabel(taskType)}
                            {suffix}
                        </>
                    }
                    value={hasLabels}
                    onChange={handleHasLabelsChange}
                />
            )}

            {showResponseSection && !hasLabels && (
                <NoLabelsModeSection
                    taskType={taskType}
                    value={noLabelsMode}
                    titleSuffix={titleSuffix}
                    onChange={(mode) =>
                        onPatch({
                            noLabelsMode: mode,
                            ...(mode === NO_LABELS_MODE_UNLABELED ? { aiLabels: [] } : {}),
                        })
                    }
                />
            )}

            {isGenerative && hasLabels && (
                <FieldRow title={outputSectionTitle} description={outputDescription}>
                    <div className="cw-dc-review-examples">
                        <div className="cw-dc-review-examples__inputs">
                            <ColumnFieldSelect
                                columnsLoading={columnsLoading}
                                error={errors.labelColumn}
                                label={outputFieldLabel}
                                showAsterisk
                                placeholder="Select"
                                searchEmptyText="No matching columns"
                                text={responseColumn || undefined}
                                items={columnItems(responseColumn, (column) =>
                                    onPatch({ labelColumn: column })
                                )}
                            />
                        </div>
                        <FieldPreviewCard
                            label={outputPreviewLabel}
                            text={responsePreviewText}
                            values={responseValues}
                            rowIndex={responsePreviewIndex}
                            rowTotal={responseValues.length}
                            columnName={responseColumn}
                            onShuffle={handleShuffleResponse}
                            shuffleAriaLabel={`Shuffle ${outputPreviewLabel.toLowerCase()}`}
                        />
                    </div>
                </FieldRow>
            )}

            {showLabelsSection && hasLabels && (
                <FieldRow title={labelsSectionTitle} description="Select example field">
                    <div className="cw-dc-labels-review">
                        <div className="cw-dc-review-examples">
                            <div className="cw-dc-review-examples__inputs">
                                <ColumnFieldSelect
                                    columnsLoading={columnsLoading}
                                    error={errors.labelColumn}
                                    label={`Which column contain the ${isNer ? 'entities' : 'labels'}? (for examples)`}
                                    showAsterisk
                                    placeholder="Select"
                                    searchEmptyText="No matching columns"
                                    text={details.labelColumn || undefined}
                                    items={columnItems(details.labelColumn, (column) =>
                                        onLabelColumnChange(column)
                                    )}
                                />
                                {showDelimiterSelect && (
                                    <div className="cw-dc-select-360 cw-dc-delimiter">
                                        <div className="cw-dc-delimiter__label">
                                            <Label
                                                label="Select labels separator (delimiter)"
                                                showAsterisk
                                                showIcon={false}
                                            />
                                            <TooltipTrigger text="Select the separator used to separate labels in the labels column. Detected automatically from the first records - change it if needed.">
                                                <span className="cw-help-icon">
                                                    <SparkIcon slug="circle-help" size="small" />
                                                </span>
                                            </TooltipTrigger>
                                        </div>
                                        <DropdownMenu
                                            selectType="single-select"
                                            size="medium"
                                            showLabel={false}
                                            placeholder="Delimiter"
                                            text={
                                                labelsDelimiterOptionLabel(
                                                    details.labelsDelimiter
                                                ) || undefined
                                            }
                                            items={LABELS_DELIMITER_OPTIONS.map((option) => ({
                                                label: option.label,
                                                state:
                                                    option.value === details.labelsDelimiter
                                                        ? 'selected'
                                                        : 'default',
                                                onClick: () =>
                                                    onPatch({ labelsDelimiter: option.value }),
                                            }))}
                                        />
                                        {errors.labelsDelimiter && (
                                            <div className="cw-field-error" role="alert">
                                                {errors.labelsDelimiter}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {Boolean(details.labelColumn) && !columnsLoading && (
                                <FieldPreviewCard
                                    label={
                                        isMultilabelClassification
                                            ? `Value of '${details.labelColumn}'`
                                            : isNer
                                              ? 'Entity'
                                              : 'Label'
                                    }
                                    text={responsePreviewText}
                                    values={responseValues}
                                    rowIndex={responsePreviewIndex}
                                    rowTotal={responseValues.length}
                                    columnName={details.labelColumn}
                                    onShuffle={handleShuffleResponse}
                                    shuffleAriaLabel="Shuffle labels raw value"
                                />
                            )}
                        </div>
                        {previewTruncated && !columnsLoading && (
                            <AlertBanner
                                feedback="warning"
                                showButton={false}
                                showCloseButton={false}
                                text={previewTruncationMessage(taskType)}
                            />
                        )}
                        <LabelMappingRows
                            taskType={taskType}
                            extractedLabels={columnsLoading ? [] : details.extractedLabels}
                            mappings={details.labelMappings}
                            availableLabels={availableLabels}
                            replaceOptions={replaceLabelOptions}
                            loading={!columnsLoading && details.labelsLoading}
                            onReplace={(label, next) =>
                                onPatch({
                                    labelMappings: { ...details.labelMappings, [label]: next },
                                })
                            }
                            onDelete={(label) =>
                                onPatch({
                                    labelMappings: { ...details.labelMappings, [label]: null },
                                })
                            }
                            onToggleTaskLabel={onToggleTaskLabel}
                        />
                        {showMissingBanner && (
                            <AlertBanner
                                feedback="warning"
                                showButton={false}
                                showCloseButton
                                text={`Missing ${isNer ? 'Entity' : 'Label'} as defined in task creation: ${missingLabels.join(', ')}`}
                                action={
                                    <MissingLabelsFixMenu
                                        isNer={isNer}
                                        isExistingTask={isExistingTask}
                                        onAddTaskLabels={handleUpdateTaskLabels}
                                        onGenerateNewData={onGenerateNewData}
                                        onAddMoreFiles={onAddMoreFiles}
                                    />
                                }
                                onCloseClick={() => setDismissedSignature(missingSignature)}
                            />
                        )}
                    </div>
                </FieldRow>
            )}

            {/* Empty response handling: only when a labels/entities/summary/
                output column is selected (hidden when has-labels switch is
                off) and empty values were found or could not be ruled out. */}
            {hasLabels && Boolean(details.labelColumn) && showEmptyResponsesSection && !columnsLoading && (
                <EmptyResponsesSection
                    taskType={taskType}
                    isMultilabel={Boolean(isMultilabel)}
                    value={
                        details.emptyMultilabelHandling ||
                        emptyResponseDefault(Boolean(isMultilabel))
                    }
                    onChange={(next) => onPatch({ emptyMultilabelHandling: next })}
                    titleSuffix={titleSuffix}
                />
            )}

            {/* Agentic tasks only (Figma 3966-23055 / 4012-19043): the
                trajectories question follows Review Outputs and reveals the
                Map Trajectory Fields block when answered Yes. */}
            {isAgentic && (
                <YesNoQuestionRow
                    testId="has-trajectories-question"
                    question={HAS_TRAJECTORIES_QUESTION}
                    value={Boolean(details.hasTrajectories)}
                    onChange={handleHasTrajectoriesChange}
                    hint={
                        !details.hasTrajectories && !providersLoading ? (
                            <NoTrajectoriesHint
                                hasNoProviders={hasNoProviders}
                                hasResponseColumn={hasLabels && Boolean(details.labelColumn)}
                                onOpenProviderSettings={onOpenProviderSettings}
                            />
                        ) : null
                    }
                >
                    {details.hasTrajectories && (
                        <TrajectoryFieldsMapper
                            slot={slot}
                            details={details}
                            columns={columns}
                            columnsLoading={columnsLoading}
                            errors={errors}
                            onPatch={onPatch}
                        />
                    )}
                    {/* Empty trajectory handling: only when a trajectory column
                        is mapped and empty values were found or could not be
                        ruled out (same rule as the empty-responses section). */}
                    {details.hasTrajectories &&
                        Boolean(details.trajectoryColumn) &&
                        showEmptyTrajectoriesSection &&
                        !columnsLoading && (
                            <>
                                <EmptyTrajectoriesSection
                                    value={details.emptyTrajectoryHandling || EMPTY_TRAJECTORIES_DEFAULT}
                                    onChange={(next) => onPatch({ emptyTrajectoryHandling: next })}
                                    titleSuffix={titleSuffix}
                                />
                                {isEmptyTrajectoryGenerateWithAi(details.emptyTrajectoryHandling) && (
                                    <p
                                        className="cw-dc-yes-no__hint"
                                        data-testid="empty-trajectories-deferred-note"
                                    >
                                        {EMPTY_TRAJECTORIES_DEFERRED_NOTE}
                                    </p>
                                )}
                            </>
                        )}
                </YesNoQuestionRow>
            )}
        </div>
    );
};

export default DatasetDetailsForm;
